import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { useBag } from "../context/BagContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const attempts = useRef(0);
  const { setQuantity } = useBag();

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      if (attempts.current >= 8) {
        setStatus("timeout");
        return;
      }
      attempts.current += 1;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          setQuantity(0);
          return;
        }
        if (["failed", "expired"].includes(res.data.payment_status)) {
          setStatus("failed");
          return;
        }
      } catch (e) {
        // Transient verification error — keep polling; after 8 attempts the user sees
        // the "Still processing" message with support guidance.
        if (process.env.NODE_ENV !== "production") {
          console.warn(`Payment status check failed (attempt ${attempts.current}/8)`, e?.response?.status || e?.message);
        }
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, setQuantity]);

  return (
    <div className="min-h-screen bg-[#F8F4EC] flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="max-w-md w-full bg-[#FFFDF9] rounded-2xl border border-[rgba(58,42,37,0.08)] shadow-[0_20px_50px_rgba(58,42,37,0.08)] p-10 sm:p-12 text-center">
        {status === "checking" && (
          <>
            <Loader2 size={40} className="animate-spin text-[#C9A45C] mx-auto" />
            <h1 className="font-serif-display text-3xl text-[#2B1F1B] mt-6">Confirming your payment…</h1>
            <p className="mt-3 text-sm font-light text-[#756A64]" data-testid="payment-checking-message">
              One moment — we're securely verifying your order with Stripe.
            </p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 size={44} className="text-[#2E7D32] mx-auto" strokeWidth={1.5} />
            <h1 className="font-serif-display text-3xl text-[#2B1F1B] mt-6">Thank you for your order!</h1>
            <p className="mt-3 text-sm font-light leading-relaxed text-[#756A64]" data-testid="payment-paid-message">
              Your payment was successful. Your CELUSTA Leave-In Conditioner is being prepared
              for shipment — a confirmation has been recorded for your order.
            </p>
          </>
        )}
        {(status === "failed" || status === "error") && (
          <>
            <AlertCircle size={44} className="text-[#C62828] mx-auto" strokeWidth={1.5} />
            <h1 className="font-serif-display text-3xl text-[#2B1F1B] mt-6">Payment not completed</h1>
            <p className="mt-3 text-sm font-light text-[#756A64]" data-testid="payment-failed-message">
              Your payment could not be confirmed. You have not been charged — please try again.
            </p>
          </>
        )}
        {status === "timeout" && (
          <>
            <AlertCircle size={44} className="text-[#C9A45C] mx-auto" strokeWidth={1.5} />
            <h1 className="font-serif-display text-3xl text-[#2B1F1B] mt-6">Still processing</h1>
            <p className="mt-3 text-sm font-light text-[#756A64]" data-testid="payment-timeout-message">
              Your payment is taking longer than expected. If you were charged, your order will be
              confirmed automatically — check back shortly or contact support.
            </p>
          </>
        )}
        <Link
          to="/"
          className="inline-block mt-8 px-8 py-3.5 bg-[#3A2A25] text-white text-xs font-semibold uppercase tracking-[0.2em] rounded-full hover:bg-[#55423A] transition-colors duration-300"
          data-testid="back-to-shop-button"
        >
          Back to Shop
        </Link>
      </div>
    </div>
  );
}
